import React, {
    Fragment,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createUseStyles, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import { CopilotContext } from '../../../Context';
import { useWeb5Context } from 'lib/Web5Context';

import Actions from '../Actions';
import Comment from '../Comment';
import Feedback from '../Feedback';

import styles from './styles';
import useChatAutoScroll from './useChatAutoScroll';

const useStyles = createUseStyles(styles, 'CopilotChat');

const Chat = memo(
    ({
        chatId,
        chatSuggestions,
        conversation,
        conversationId,
        isDanaWritting,
        loadingPhrase,
        onCopy,
        onEmail,
        onFeedback,
        onVote,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const theme = useTheme();
        const { t } = useWeb5Context();
        const { chats, setVote } = useContext(CopilotContext);

        // ========================================
        // REFS
        // ========================================
        const feedbackIdRef = useRef(null);
        const bottomRef = useRef(null);

        // ========================================
        // LOCAL STATE
        // ========================================
        const [activeVote, setActiveVote] = useState('');
        const [feedbackText, setFeedbackText] = useState('');
        const [completedTypewriterIds, setCompletedTypewriterIds] = useState({});

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const chatState = useMemo(() => chats?.[chatId] ?? {}, [chats, chatId]);
        const { reports, tables } = chatState;

        // Ordered list of assistant interaction IDs for typewriter sequencing
        const assistantIds = useMemo(() => {
            if (!conversation) return [];
            return Object.entries(conversation)
                .filter(([, value]) => value.role !== 'user')
                .map(([key]) => key);
        }, [conversation]);

        const userMessageCount = useMemo(() => {
            if (!conversation) return 0;

            return Object.values(conversation).filter((item) => item.role === 'user').length;
        }, [conversation]);

        const scrollToBottom = useChatAutoScroll({ bottomRef, conversation, userMessageCount });

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleTypewriterComplete = useCallback((interactionId) => {
            setCompletedTypewriterIds((prev) => ({ ...prev, [interactionId]: true }));
        }, []);

        const isTypewriterPaused = useCallback(
            (interactionId) => {
                const idx = assistantIds.indexOf(interactionId);
                if (idx <= 0) return false;
                for (let i = 0; i < idx; i++) {
                    if (!completedTypewriterIds[assistantIds[i]]) return true;
                }
                return false;
            },
            [assistantIds, completedTypewriterIds],
        );

        const handleOnCopy = useCallback(
            (value) => () => {
                if (!value.content && value.role === 'user') return null;
                onCopy && onCopy(value);
            },
            [onCopy],
        );

        const getInteractionIndex = useCallback(
            (interactionId) => {
                const responsesIds = Object.entries(conversation).reduce((arr, [key, value]) => {
                    if (value.role !== 'user') {
                        arr.push(key);
                    }
                    return arr;
                }, []);

                const index = responsesIds.indexOf(interactionId);
                if (index === -1) return null;
                return index;
            },
            [conversation],
        );

        const keepVote = useCallback((vote) => {
            setActiveVote(vote || '');
        }, []);

        const handleVote = useCallback(
            (interactionId) => (newVote) => () => {
                if (!newVote || !onVote) return;
                const index = getInteractionIndex(interactionId);
                if (index === null) return;

                onVote({ conversationId, index, vote: newVote }).then((id) => {
                    feedbackIdRef.current = id;
                    setVote({ chatId, interactionId, vote: newVote });
                    keepVote(newVote);
                });
            },
            [chatId, conversationId, getInteractionIndex, keepVote, onVote, setVote],
        );

        const onCloseFeedback = useCallback(() => {
            setFeedbackText('');
            keepVote();
            feedbackIdRef.current = null;
        }, [keepVote]);

        const onConfirmFeedback = useCallback(() => {
            if (!onFeedback) return;
            onFeedback({
                feedback: feedbackText,
                id: feedbackIdRef.current,
            }).then(() => {
                onCloseFeedback();
            });
        }, [feedbackText, onCloseFeedback, onFeedback]);

        // ========================================
        // RENDER HELPERS
        // ========================================
        const renderInteraction = useCallback(
            (key, value) => {
                if (value.error) {
                    return (
                        <div className={classes.error} key={key}>
                            <Icon name="infoOutlined" />
                            <Text color={theme.colors.utility.textSecondary} type="body">
                                {t('label_dana_chat_error_not_sent')}
                            </Text>
                        </div>
                    );
                }

                const isAssistant = value.role !== 'user';

                const commentProps = {
                    audioData: value.audioData,
                    creationDate: value.creationDate || null,
                    delta: value.delta,
                    disableVote: value.disableVote,
                    finished: value.isFinished,
                    interactionId: key,
                    isError: value.isError || false,
                    isProgress: !value.isFinished,
                    isUser: !isAssistant,
                    loading: value.isLoading || false,
                    currentLoadingPhrase: loadingPhrase,
                    onCopy: onCopy && isAssistant && handleOnCopy(value),
                    onEmail,
                    onContentUpdate: isAssistant ? scrollToBottom : undefined,
                    onTypewriterComplete: isAssistant
                        ? () => handleTypewriterComplete(key)
                        : undefined,
                    onVote: !!onVote && !!onFeedback ? handleVote(key) : undefined,
                    reports,
                    tables,
                    text: value.content,
                    typewriterPaused: isAssistant ? isTypewriterPaused(key) : false,
                    vote: value.vote,
                    artifacts: value.artifacts,
                };

                const showActions = !isAssistant || completedTypewriterIds[key];

                const actionsProps = {
                    actions: value.actions,
                    finished: value.isFinished,
                };

                return (
                    <Fragment key={key}>
                        <Comment {...commentProps} />
                        {showActions && <Actions {...actionsProps} />}
                    </Fragment>
                );
            },
            [
                classes.error,
                completedTypewriterIds,
                handleOnCopy,
                scrollToBottom,
                handleTypewriterComplete,
                handleVote,
                isTypewriterPaused,
                loadingPhrase,
                onCopy,
                onEmail,
                onFeedback,
                onVote,
                reports,
                t,
                tables,
                theme.colors.utility.textSecondary,
            ],
        );

        // ========================================
        // MEMOIZED COMPONENT PROPS
        // ========================================
        // Determine which interactions are visible based on previous typewriter completion
        const visibleInteractionKeys = useMemo(() => {
            if (!conversation) return new Set();
            const entries = Object.entries(conversation);
            const visible = new Set();
            let lastAssistantKey = null;

            for (const [key, value] of entries) {
                // If there's a previous assistant message that hasn't completed, stop
                if (lastAssistantKey && !completedTypewriterIds[lastAssistantKey]) break;
                visible.add(key);
                if (value.role !== 'user') lastAssistantKey = key;
            }

            return visible;
        }, [conversation, completedTypewriterIds]);

        const allTypewritersComplete = useMemo(() => {
            if (assistantIds.length === 0) return true;
            return assistantIds.every((id) => completedTypewriterIds[id]);
        }, [assistantIds, completedTypewriterIds]);

        const feedbackProps = useMemo(
            () => ({
                onChange: setFeedbackText,
                onClose: onCloseFeedback,
                onConfirm: onConfirmFeedback,
                onRequestClose: onCloseFeedback,
                value: feedbackText,
                vote: activeVote,
            }),
            [activeVote, feedbackText, onCloseFeedback, onConfirmFeedback],
        );

        // ========================================
        // EFFECTS
        // ========================================
        // Scroll to bottom when new interactions, actions, or suggestions become visible
        useEffect(() => {
            scrollToBottom();
        }, [
            scrollToBottom,
            visibleInteractionKeys,
            completedTypewriterIds,
            allTypewritersComplete,
            chatSuggestions,
        ]);

        // ========================================
        // RENDER
        // ========================================
        if (!conversation) return null;

        return (
            <div className={classes.root}>
                {Object.entries(conversation).map(
                    ([key, value]) =>
                        visibleInteractionKeys.has(key) && renderInteraction(key, value),
                )}
                {!isDanaWritting && allTypewritersComplete && chatSuggestions}
                <Feedback {...feedbackProps} />
                <div ref={bottomRef} />
            </div>
        );
    },
);

export default Chat;
